import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Lock, User, Eye, EyeOff, Shield, AlertCircle, HardDrive } from 'lucide-react'
import { api, setToken } from '../lib/api'
import { trackEvent } from '../lib/tracking'

export default function AdminLogin() {
  const nav = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    trackEvent('admin_login_attempt', { username })
    try {
      const res = await api.login(username, password)
      setToken(res.access_token)
      localStorage.setItem('cs_admin_user', JSON.stringify(res.user))
      trackEvent('admin_login_success', { username: res.user.username })
      nav('/admin')
    } catch (err: any) {
      setError(err.message || 'Login failed')
      trackEvent('admin_login_failed', { username, error: err.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-void-black flex items-center justify-center p-4">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden -z-10">
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-nebula-purple/30 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 -right-40 h-96 w-96 rounded-full bg-cosmic-blue/20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 h-[40rem] w-[40rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-aurora-cyan/5 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-cosmic shadow-2xl shadow-nebula-purple/40">
            <HardDrive className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-3xl font-bold gradient-text">Cloudsize</h1>
          <p className="mt-1 text-sm text-star-white/50">Admin Control Panel</p>
        </div>

        {/* Card */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-8 backdrop-blur-xl shadow-2xl">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-nebula-purple/20">
              <Shield className="h-5 w-5 text-nebula-purple" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-star-white">Secure Sign In</h2>
              <p className="text-xs text-star-white/50">Authorized personnel only</p>
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-star-white/60">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-star-white/40" />
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-star-white placeholder:text-star-white/30 focus:border-nebula-purple/50 focus:outline-none focus:ring-2 focus:ring-nebula-purple/20"
                  placeholder="admin"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-star-white/60">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-star-white/40" />
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-11 pr-11 text-star-white placeholder:text-star-white/30 focus:border-nebula-purple/50 focus:outline-none focus:ring-2 focus:ring-nebula-purple/20"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-star-white/50 hover:bg-white/10 hover:text-star-white transition"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-gradient-cosmic px-6 py-3.5 font-semibold text-white shadow-lg shadow-nebula-purple/30 transition hover:scale-[1.02] hover:shadow-nebula-purple/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in…' : 'Sign In to Dashboard'}
            </button>
          </form>

          <div className="mt-6 rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-3 text-xs text-yellow-300/80">
            <strong>Note:</strong> This sign-in attempt is logged with your IP, device, browser, and location for security.
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-star-white/40">
          © 2026 Cloudsize · <a href="/" className="hover:text-star-white transition">Back to site</a>
        </p>
      </motion.div>
    </div>
  )
}
